import { useState, useEffect, useContext, useRef } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import ArticleEditor from '../components/ArticleEditor/ArticleEditor'
import './Publications.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const TYPE_LABELS = { video: 'Vidéo', carousel: 'Carrousel', article: 'Article' }

const ACCEPTED = {
    video:    'video/mp4,video/quicktime,video/webm,video/x-msvideo',
    carousel: 'image/jpeg,image/png,image/webp,image/gif,image/avif',
    article:  'image/jpeg,image/png,image/webp,image/avif',
}

// ─── Upload helpers ───────────────────────────────────────────────────────────

async function uploadFile(brandId, file, onProgress) {
    // 1. Init
    const { uploadId, presignedUrl } = await apiFetch(
        `/brand/${brandId}/publications/upload/init`,
        {
            method: 'POST',
            body: JSON.stringify({ contentType: file.type, contentLength: file.size, filename: file.name }),
        }
    )

    // 2. PUT binaire directement sur MinIO
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', presignedUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 80))
        }
        xhr.onload = () => xhr.status < 400 ? resolve() : reject(new Error(`Upload HTTP ${xhr.status}`))
        xhr.onerror = () => reject(new Error('Erreur réseau upload'))
        xhr.send(file)
    })

    // 3. Confirm
    const { id: mediaId } = await apiFetch(
        `/brand/${brandId}/publications/upload/confirm`,
        { method: 'POST', body: JSON.stringify({ uploadId, contentType: file.type }) }
    )

    // 4. Poll jusqu'à ready
    if (onProgress) onProgress(85)
    for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 2000))
        const media = await apiFetch(`/brand/${brandId}/publications/media/${mediaId}`)
        if (media.status === 'ready') { if (onProgress) onProgress(100); return mediaId }
        if (media.status === 'error') throw new Error('Traitement du média échoué')
    }
    throw new Error('Délai d\'attente dépassé pour le traitement du média')
}

// ─── Composant principal ──────────────────────────────────────────────────────

const Publications = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand

    const [winkUser, setWinkUser]           = useState(undefined) // undefined = chargement
    const [publications, setPublications]   = useState([])
    const [loading, setLoading]             = useState(true)
    const [creatingAccount, setCreatingAccount] = useState(false)

    // Modal création
    const [showModal, setShowModal]         = useState(false)
    const [step, setStep]                   = useState('choose') // choose | upload | finalize
    const [pubType, setPubType]             = useState(null)     // video | carousel | article

    // Upload
    const [files, setFiles]                 = useState([])       // File[]
    const [uploadProgress, setUploadProgress] = useState([])     // number[]
    const [uploading, setUploading]         = useState(false)
    const [uploadedMediaIds, setUploadedMediaIds] = useState([]) // string[]
    const [uploadError, setUploadError]     = useState(null)

    // Finalisation
    const [pubTitle, setPubTitle]               = useState('')
    const [articleEditorPages, setArticleEditorPages] = useState([])
    const [submitting, setSubmitting]           = useState(false)
    const [submitError, setSubmitError]         = useState(null)

    // Produits associés
    const [brandProducts, setBrandProducts]     = useState([])
    const [loadingProducts, setLoadingProducts] = useState(false)
    const [selectedProductIds, setSelectedProductIds] = useState([])

    const fileInputRef = useRef(null)

    // ─── Chargement initial ───────────────────────────────────────────────────

    const loadWinkAccount = async () => {
        if (!brandId) return
        try {
            const data = await apiFetch(`/brand/${brandId}/wink-account`)
            setWinkUser(data.winkUser || null)
        } catch {
            setWinkUser(null)
        }
    }

    const loadPublications = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/publications`)
            setPublications(data.items || [])
        } catch {
            setPublications([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadWinkAccount()
        loadPublications()
    }, [brandId])

    useEffect(() => {
        if (step === 'finalize') loadBrandProducts()
    }, [step])

    // ─── Compte Wink ──────────────────────────────────────────────────────────

    const handleCreateAccount = async () => {
        if (!brandId) return
        setCreatingAccount(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/wink-account`, { method: 'POST' })
            setWinkUser(data.winkUser)
        } catch (err) {
            alert(err.message)
        } finally {
            setCreatingAccount(false)
        }
    }

    const handleSyncAccount = async () => {
        if (!brandId) return
        setCreatingAccount(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/wink-account`, { method: 'POST' })
            setWinkUser(data.winkUser)
        } catch (err) {
            alert(err.message)
        } finally {
            setCreatingAccount(false)
        }
    }

    // ─── Modal helpers ─────────────────────────────────────────────────────────

    const openModal = () => {
        setStep('choose')
        setPubType(null)
        setFiles([])
        setUploadProgress([])
        setUploadedMediaIds([])
        setUploadError(null)
        setPubTitle('')
        setArticleEditorPages([])
        setSelectedProductIds([])
        setSubmitError(null)
        setShowModal(true)
    }

    const closeModal = () => setShowModal(false)

    const handleTypeSelect = (t) => {
        setPubType(t)
        if (t === 'article') {
            setStep('article-editor')
        } else {
            setStep('upload')
        }
    }

    // ─── Upload ────────────────────────────────────────────────────────────────

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || [])
        if (pubType === 'video') setFiles(selected.slice(0, 1))
        else if (pubType === 'carousel') setFiles(selected.slice(0, 10))
        else setFiles(selected.slice(0, 1))
        e.target.value = ''
    }

    const handleUpload = async () => {
        if (!files.length) return
        setUploading(true)
        setUploadError(null)
        setUploadProgress(files.map(() => 0))

        try {
            const ids = await Promise.all(
                files.map((file, idx) =>
                    uploadFile(brandId, file, (p) =>
                        setUploadProgress(prev => prev.map((v, i) => i === idx ? p : v))
                    )
                )
            )
            setUploadedMediaIds(ids)
            setStep('finalize')
        } catch (err) {
            setUploadError(err.message)
        } finally {
            setUploading(false)
        }
    }

    // ─── Produits ──────────────────────────────────────────────────────────────

    const loadBrandProducts = async () => {
        if (!brandId || brandProducts.length > 0) return
        setLoadingProducts(true)
        try {
            const data = await apiFetch(`/brand/${brandId}/publications/products`)
            setBrandProducts(data.products || [])
        } catch {
            setBrandProducts([])
        } finally {
            setLoadingProducts(false)
        }
    }

    const toggleProduct = (id) =>
        setSelectedProductIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        )

    // ─── Finalisation ──────────────────────────────────────────────────────────

    const handleSubmit = async () => {
        setSubmitting(true)
        setSubmitError(null)
        try {
            const body = { type: pubType, title: pubTitle || null, productIds: selectedProductIds }
            if (pubType === 'article') {
                if (!articleEditorPages.length) throw new Error('Ajoutez au moins une page dans l\'éditeur')
                body.pages = articleEditorPages
            } else {
                body.mediaIds = uploadedMediaIds
            }
            await apiFetch(`/brand/${brandId}/publications`, {
                method: 'POST',
                body: JSON.stringify(body),
            })
            closeModal()
            loadPublications()
        } catch (err) {
            setSubmitError(err.message)
        } finally {
            setSubmitting(false)
        }
    }

    // ─── Supprimer une publication ─────────────────────────────────────────────

    const handleDelete = async (pubId) => {
        if (!window.confirm('Supprimer cette publication ?')) return
        try {
            await apiFetch(`/brand/${brandId}/publications/${pubId}`, { method: 'DELETE' })
            setPublications(prev => prev.filter(p => p._id !== pubId))
        } catch (err) {
            alert(err.message)
        }
    }

    // ─── Miniature publication ─────────────────────────────────────────────────

    const getPoster = (pub) => {
        if (pub.type === 'video') return pub.poster || null
        if (pub.type === 'carousel') return pub.slides?.[0]?.poster || pub.poster || null
        return null
    }

    // ─── Rendu ────────────────────────────────────────────────────────────────

    if (!brandId) {
        return <div className="pub-page"><p>Aucune marque associée à votre compte.</p></div>
    }

    return (
        <div className="pub-page">
            {/* ── Compte Wink ── */}
            <section className="pub-section">
                <div className="pub-section-header">
                    <h2>Compte Wink</h2>
                </div>
                {winkUser === undefined ? (
                    <p className="pub-loading">Chargement…</p>
                ) : winkUser ? (
                    <div className="pub-wink-card">
                        <img
                            src={winkUser.imageUrl || 'https://via.placeholder.com/64'}
                            alt="avatar"
                            className="pub-wink-avatar"
                        />
                        <div className="pub-wink-info">
                            <span className="pub-wink-username">@{winkUser.username}</span>
                            <span className="pub-wink-label">Compte actif dans Wink</span>
                        </div>
                        <button
                            className="pub-btn-outline"
                            onClick={handleSyncAccount}
                            disabled={creatingAccount}
                        >
                            {creatingAccount ? 'Synchronisation…' : 'Synchroniser'}
                        </button>
                    </div>
                ) : (
                    <div className="pub-wink-empty">
                        <p>Aucun compte Wink associé. Créez-en un pour publier du contenu au nom de votre marque.</p>
                        <button
                            className="pub-btn-primary"
                            onClick={handleCreateAccount}
                            disabled={creatingAccount}
                        >
                            {creatingAccount ? 'Création…' : 'Créer le compte Wink'}
                        </button>
                    </div>
                )}
            </section>

            {/* ── Publications ── */}
            <section className="pub-section">
                <div className="pub-section-header">
                    <h2>Publications</h2>
                    {winkUser && (
                        <button className="pub-btn-primary" onClick={openModal}>
                            + Nouvelle publication
                        </button>
                    )}
                </div>

                {loading ? (
                    <p className="pub-loading">Chargement…</p>
                ) : publications.length === 0 ? (
                    <p className="pub-empty">Aucune publication pour le moment.</p>
                ) : (
                    <div className="pub-grid">
                        {publications.map(pub => (
                            <div key={pub._id} className="pub-card">
                                <div className="pub-card-thumb">
                                    {getPoster(pub) ? (
                                        <img src={getPoster(pub)} alt="" />
                                    ) : (
                                        <div className="pub-card-thumb-placeholder">
                                            {pub.type === 'article' ? '📄' : pub.type === 'video' ? '🎬' : '🖼️'}
                                        </div>
                                    )}
                                    <span className="pub-card-type">{TYPE_LABELS[pub.type]}</span>
                                </div>
                                <div className="pub-card-body">
                                    <p className="pub-card-title">{pub.title || '(Sans titre)'}</p>
                                    <p className="pub-card-date">
                                        {new Date(pub.publishedAt || pub.createdAt).toLocaleDateString('fr-FR')}
                                    </p>
                                </div>
                                <button
                                    className="pub-card-delete"
                                    onClick={() => handleDelete(pub._id)}
                                    title="Supprimer"
                                >✕</button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* ── Modal création ── */}
            {showModal && (
                <div className="pub-modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
                    <div className={`pub-modal${step === 'article-editor' ? ' pub-modal--editor' : ''}`}>
                        {step !== 'article-editor' && (
                            <div className="pub-modal-header">
                                <h3>
                                    {step === 'choose' && 'Choisir le type'}
                                    {step === 'upload' && `Importer ${pubType === 'video' ? 'une vidéo' : 'des images'}`}
                                    {step === 'finalize' && 'Finaliser la publication'}
                                </h3>
                                <button className="pub-modal-close" onClick={closeModal}>✕</button>
                            </div>
                        )}

                        {/* Étape article-editor */}
                        {step === 'article-editor' && (
                            <ArticleEditor
                                onBack={() => setStep('choose')}
                                onDone={(pages) => { setArticleEditorPages(pages); setStep('finalize') }}
                                uploadBgFile={(file, onProgress) => uploadFile(brandId, file, onProgress)}
                                brandProducts={brandProducts}
                            />
                        )}

                        {/* Étape 1 : choix du type */}
                        {step === 'choose' && (
                            <div className="pub-type-grid">
                                {[
                                    { key: 'video',    label: 'Vidéo',     icon: '🎬', desc: 'MP4, MOV, WEBM — max 500 Mo' },
                                    { key: 'carousel', label: 'Carrousel', icon: '🖼️', desc: '1 à 10 images — max 20 Mo/image' },
                                    { key: 'article',  label: 'Article',   icon: '📄', desc: 'Texte libre, pas de média requis' },
                                ].map(t => (
                                    <button
                                        key={t.key}
                                        className="pub-type-btn"
                                        onClick={() => handleTypeSelect(t.key)}
                                    >
                                        <span className="pub-type-icon">{t.icon}</span>
                                        <span className="pub-type-label">{t.label}</span>
                                        <span className="pub-type-desc">{t.desc}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Étape 2 : upload */}
                        {step === 'upload' && (
                            <div className="pub-upload-area">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED[pubType]}
                                    multiple={pubType === 'carousel'}
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />

                                {files.length === 0 ? (
                                    <button
                                        className="pub-drop-zone"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <span className="pub-drop-icon">
                                            {pubType === 'video' ? '🎬' : '🖼️'}
                                        </span>
                                        <span>
                                            {pubType === 'video'
                                                ? 'Cliquez pour sélectionner une vidéo'
                                                : 'Cliquez pour sélectionner 1 à 10 images'}
                                        </span>
                                    </button>
                                ) : (
                                    <div className="pub-files-list">
                                        {files.map((f, i) => (
                                            <div key={i} className="pub-file-row">
                                                <span className="pub-file-name">{f.name}</span>
                                                {uploading && (
                                                    <div className="pub-progress-bar">
                                                        <div
                                                            className="pub-progress-fill"
                                                            style={{ width: `${uploadProgress[i] || 0}%` }}
                                                        />
                                                    </div>
                                                )}
                                                {!uploading && (
                                                    <span className="pub-file-size">
                                                        {(f.size / 1024 / 1024).toFixed(1)} Mo
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                        {!uploading && (
                                            <button
                                                className="pub-btn-outline pub-btn-sm"
                                                onClick={() => fileInputRef.current?.click()}
                                            >
                                                Changer
                                            </button>
                                        )}
                                    </div>
                                )}

                                {uploadError && <p className="pub-error">{uploadError}</p>}

                                <div className="pub-modal-actions">
                                    <button
                                        className="pub-btn-outline"
                                        onClick={() => setStep('choose')}
                                        disabled={uploading}
                                    >
                                        Retour
                                    </button>
                                    <button
                                        className="pub-btn-primary"
                                        onClick={handleUpload}
                                        disabled={uploading || files.length === 0}
                                    >
                                        {uploading ? 'Traitement…' : 'Suivant'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Étape 3 : finalisation */}
                        {step === 'finalize' && (
                            <div className="pub-finalize">
                                <label className="pub-label">
                                    Titre <span className="pub-optional">(optionnel)</span>
                                    <input
                                        className="pub-input"
                                        type="text"
                                        maxLength={100}
                                        placeholder="Titre de la publication"
                                        value={pubTitle}
                                        onChange={e => setPubTitle(e.target.value)}
                                    />
                                </label>

                                {pubType === 'article' && (
                                    <p className="pub-loading">
                                        {articleEditorPages.length} page{articleEditorPages.length !== 1 ? 's' : ''} créée{articleEditorPages.length !== 1 ? 's' : ''} dans l'éditeur.
                                    </p>
                                )}

                                {/* Produits associés */}
                                <div className="pub-label">
                                    Produits associés
                                    <span className="pub-optional"> (optionnel)</span>
                                </div>
                                {loadingProducts ? (
                                    <p className="pub-loading">Chargement des produits…</p>
                                ) : brandProducts.length === 0 ? (
                                    <p className="pub-empty">Aucun produit publié. Publiez des produits depuis la section Produits pour les associer.</p>
                                ) : (
                                    <div className="pub-products-grid">
                                        {brandProducts.map(p => (
                                            <button
                                                key={p.id}
                                                type="button"
                                                className={`pub-product-card${selectedProductIds.includes(String(p.id)) ? ' pub-product-card--sel' : ''}`}
                                                onClick={() => toggleProduct(String(p.id))}
                                            >
                                                <div className="pub-product-img">
                                                    {p.image
                                                        ? <img src={p.image} alt={p.name} />
                                                        : <span>📦</span>
                                                    }
                                                    {selectedProductIds.includes(String(p.id)) && (
                                                        <div className="pub-product-check">✓</div>
                                                    )}
                                                </div>
                                                <p className="pub-product-name">{p.name}</p>
                                                <p className="pub-product-price">{p.price} {p.currency}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {submitError && <p className="pub-error">{submitError}</p>}

                                <div className="pub-modal-actions">
                                    <button
                                        className="pub-btn-outline"
                                        onClick={() => setStep(pubType === 'article' ? 'article-editor' : 'upload')}
                                        disabled={submitting}
                                    >
                                        Retour
                                    </button>
                                    <button
                                        className="pub-btn-primary"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Publication…' : 'Publier'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Publications
